// Local storage utilities for mock exam progress persistence

const storageKey = (examVersion) => examVersion === 'v2'
  ? 'hamstudent-mock-exam-v2-progress'
  : 'hamstudent-mock-exam-progress';
const EXPIRATION_DAYS = 7; // Expire saved progress after 7 days

// Check if localStorage is available
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Create exam progress object with timestamp
const createProgressData = (examState) => {
  return {
    timestamp: Date.now(),
    version: '1.0', // Version for future compatibility
    ...examState
  };
};

// Check if saved progress is expired
const isProgressExpired = (timestamp) => {
  if (!timestamp) return true;
  const now = Date.now();
  const expirationTime = EXPIRATION_DAYS * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  return (now - timestamp) > expirationTime;
};

// Save exam progress to localStorage
export const saveExamProgress = (examState, examVersion = 'v1') => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage not available - cannot save exam progress');
    return false;
  }

  try {
    const progressData = createProgressData(examState);
    localStorage.setItem(storageKey(examVersion), JSON.stringify(progressData));
    return true;
  } catch (error) {
    console.error('Failed to save exam progress:', error);
    return false;
  }
};

// Load exam progress from localStorage
export const loadExamProgress = (examVersion = 'v1') => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const storedData = localStorage.getItem(storageKey(examVersion));
    if (!storedData) {
      return null;
    }

    const progressData = JSON.parse(storedData);
    
    // Check if progress is expired
    if (isProgressExpired(progressData.timestamp)) {
      clearExamProgress(examVersion); // Clean up expired data
      return null;
    }

    // Validate required fields
    if (!progressData.questions || !progressData.selectedAnswers || 
        typeof progressData.currentQuestion !== 'number') {
      console.warn('Invalid saved exam progress data');
      clearExamProgress(examVersion); // Clean up invalid data
      return null;
    }

    return {
      questions: progressData.questions,
      selectedAnswers: progressData.selectedAnswers,
      currentQuestion: progressData.currentQuestion,
      examStarted: progressData.examStarted,
      timestamp: progressData.timestamp
    };
  } catch (error) {
    console.error('Failed to load exam progress:', error);
    clearExamProgress(examVersion); // Clean up corrupted data
    return null;
  }
};

// Clear saved exam progress
export const clearExamProgress = (examVersion = 'v1') => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(storageKey(examVersion));
    return true;
  } catch (error) {
    console.error('Failed to clear exam progress:', error);
    return false;
  }
};

// Check if saved progress exists
export const hasSavedProgress = (examVersion = 'v1') => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  const storedData = localStorage.getItem(storageKey(examVersion));
  if (!storedData) {
    return false;
  }

  try {
    const progressData = JSON.parse(storedData);
    return !isProgressExpired(progressData.timestamp);
  } catch (error) {
    console.error('Failed to check saved progress:', error);
    clearExamProgress(examVersion); // Clean up corrupted data
    return false;
  }
};

// Get formatted timestamp of saved progress
export const getSavedProgressTimestamp = (examVersion = 'v1') => {
  const progress = loadExamProgress(examVersion);
  if (!progress) {
    return null;
  }

  try {
    return new Date(progress.timestamp).toLocaleString();
  } catch (error) {
    return null;
  }
};

// Get progress summary for display
export const getProgressSummary = (examVersion = 'v1') => {
  const progress = loadExamProgress(examVersion);
  if (!progress) {
    return null;
  }

  const answeredCount = Object.keys(progress.selectedAnswers).length;
  const totalQuestions = progress.questions.length;
  const currentSection = progress.currentQuestion < 40 ? 'Regulations' : 'Technical';
  
  return {
    timestamp: getSavedProgressTimestamp(examVersion),
    currentQuestion: progress.currentQuestion + 1,
    totalQuestions,
    answeredCount,
    currentSection,
    progressPercentage: Math.round(((progress.currentQuestion + 1) / totalQuestions) * 100)
  };
};

export default {
  saveExamProgress,
  loadExamProgress,
  clearExamProgress,
  hasSavedProgress,
  getSavedProgressTimestamp,
  getProgressSummary
};